import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ALLERGEN_LABELS,
  ALLERGENS,
  DIET_LABELS,
  DIETS,
  type HouseholdPersonDto,
  type PersonPreferenceDto,
  type PreferenceKind,
} from '@fewerbags/schema';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { IngredientAutocomplete } from '../ingredients/IngredientAutocomplete';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import {
  addPreference,
  draftFromProfile,
  learnedOf,
  learnedText,
  mergeConfirmed,
  NOTES_MAX,
  preferencesPatch,
  profileKeys,
  profilePatch,
  removePreference,
  strengthLabel,
  type PreferenceChip,
  type ProfileDraft,
} from '../../lib/profiles';
import { ChipToggles } from './ChipToggles';

/**
 * One person's eating profile (D31): allergies and diets (hard filters for
 * the planner), notes, stated likes/dislikes, and the learned guesses to
 * confirm or reject. Read-only when the caller may not edit this person.
 */
export function PersonProfileSheet({
  person,
  canEdit,
  onClose,
}: {
  person: HouseholdPersonDto;
  canEdit: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const prefsKey = profileKeys.preferences(person.id);
  const prefs = useQuery({
    queryKey: prefsKey,
    queryFn: () => api.households.people.preferences(person.id),
  });
  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  const [search, setSearch] = useState('');
  const [addKind, setAddKind] = useState<PreferenceKind>('dislike');

  // Seed once, when the preferences first arrive for this opening.
  useEffect(() => {
    if (prefs.data && draft === null) setDraft(draftFromProfile(person, prefs.data.preferences));
  }, [prefs.data, draft, person]);

  const list = prefs.data?.preferences ?? [];

  const save = useMutation({
    mutationFn: async (d: ProfileDraft) => {
      const profile = profilePatch(person, d);
      if (profile) await api.households.people.updateProfile(person.id, profile);
      const stated = preferencesPatch(list, d);
      if (stated) {
        const res = await api.households.people.putPreferences(person.id, stated);
        qc.setQueryData(prefsKey, res);
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: queryKeys.people });
      onClose();
    },
  });

  const learn = useMutation({
    mutationFn: async (v: { action: 'confirm' | 'reject'; entry: PersonPreferenceDto }) =>
      api.households.people.putPreferences(person.id, { [v.action]: [v.entry.ingredientId] }),
    onSuccess: (res, v) => {
      qc.setQueryData(prefsKey, res);
      if (v.action === 'confirm') setDraft((d) => (d ? mergeConfirmed(d, [v.entry]) : d));
      void qc.invalidateQueries({ queryKey: queryKeys.people, exact: true });
    },
  });

  const learned = draft ? learnedOf(list, draft) : [];
  const dirty =
    draft !== null &&
    (profilePatch(person, draft) !== null || preferencesPatch(list, draft) !== null);

  return (
    <Dialog open onClose={onClose} title={`${person.name}’s profile`}>
      {prefs.isError ? <Alert tone="error">{messageForError(prefs.error)}</Alert> : null}
      {!canEdit ? <Alert>Only an owner or adult can change someone else’s profile.</Alert> : null}
      {draft === null ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : (
        <form
          noValidate
          className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto pr-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (dirty) save.mutate(draft);
            else onClose();
          }}
        >
          <ChipToggles
            label="Allergies"
            hint="Never planned for meals they eat."
            keys={ALLERGENS}
            labels={ALLERGEN_LABELS}
            value={draft.allergens}
            tone="danger"
            disabled={!canEdit}
            onChange={(allergens) => setDraft({ ...draft, allergens })}
          />
          <ChipToggles
            label="Diets"
            keys={DIETS}
            labels={DIET_LABELS}
            value={draft.diets}
            disabled={!canEdit}
            onChange={(diets) => setDraft({ ...draft, diets })}
          />

          <fieldset className="flex flex-col gap-2" disabled={!canEdit}>
            <legend className="text-sm font-medium text-ink">Likes / dislikes</legend>
            <p className="-mt-1 text-xs text-muted">
              Dislikes make a recipe come up less often; they never rule it out.
            </p>
            <PreferenceList
              label="Likes"
              chips={draft.likes}
              canEdit={canEdit}
              onRemove={(id) => setDraft(removePreference(draft, 'like', id))}
            />
            <PreferenceList
              label="Dislikes"
              chips={draft.dislikes}
              canEdit={canEdit}
              onRemove={(id) => setDraft(removePreference(draft, 'dislike', id))}
            />
            {canEdit ? (
              <div className="flex flex-col gap-2 rounded-lg bg-cream p-3">
                <div role="radiogroup" aria-label="Add as" className="flex gap-2">
                  {(['like', 'dislike'] as const).map((k) => (
                    <button
                      key={k}
                      type="button"
                      role="radio"
                      aria-checked={addKind === k}
                      onClick={() => setAddKind(k)}
                      className={`min-h-11 flex-1 rounded-lg border px-3 text-sm font-medium ${
                        addKind === k
                          ? 'border-brand bg-brand text-white'
                          : 'border-line bg-surface text-ink'
                      }`}
                    >
                      {k === 'like' ? 'Add a like' : 'Add a dislike'}
                    </button>
                  ))}
                </div>
                <IngredientAutocomplete
                  id={`pref-search-${person.id}`}
                  label="Ingredient"
                  placeholder="e.g. mushrooms"
                  value={search}
                  onChange={setSearch}
                  onSelect={(ing) => {
                    setDraft(
                      addPreference(draft, addKind, {
                        ingredientId: ing.id,
                        name: ing.canonicalName,
                      }),
                    );
                    setSearch('');
                  }}
                />
              </div>
            ) : null}
          </fieldset>

          {learned.length ? (
            <section aria-labelledby={`learned-${person.id}`} className="flex flex-col gap-2">
              <h3 id={`learned-${person.id}`} className="text-sm font-medium text-ink">
                We think …
              </h3>
              <p className="-mt-1 text-xs text-muted">
                Guessed from ratings and swaps. Confirm to keep it, or tell us it’s wrong.
              </p>
              {learn.isError ? <Alert tone="error">{messageForError(learn.error)}</Alert> : null}
              <ul className="flex flex-col divide-y divide-line">
                {learned.map((p) => (
                  <li key={p.ingredientId} className="flex flex-col gap-2 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-ink">{learnedText(person.name, p)}</span>
                      <StrengthBar strength={p.strength} />
                    </div>
                    {canEdit ? (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          block={false}
                          disabled={learn.isPending}
                          onClick={() => learn.mutate({ action: 'confirm', entry: p })}
                        >
                          Confirm
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          block={false}
                          disabled={learn.isPending}
                          onClick={() => learn.mutate({ action: 'reject', entry: p })}
                        >
                          Not really
                        </Button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <div className="flex flex-col gap-1">
            <label htmlFor={`person-notes-${person.id}`} className="text-sm font-medium text-ink">
              Notes
            </label>
            <textarea
              id={`person-notes-${person.id}`}
              rows={3}
              maxLength={NOTES_MAX}
              disabled={!canEdit}
              placeholder="e.g. no raw onion"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              className="rounded-lg border border-line bg-surface px-3 py-2 text-base text-ink placeholder:text-muted disabled:opacity-60"
            />
            <p className="text-xs text-muted">
              {draft.notes.length} / {NOTES_MAX}
            </p>
          </div>

          {save.isError ? <Alert tone="error">{messageForError(save.error)}</Alert> : null}
          <div className="flex flex-col gap-2 sm:flex-row-reverse">
            {canEdit ? (
              <Button type="submit" block={false} disabled={save.isPending || !dirty}>
                Save profile
              </Button>
            ) : null}
            <Button type="button" variant="ghost" block={false} onClick={onClose}>
              {canEdit ? 'Cancel' : 'Close'}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

function PreferenceList({
  label,
  chips,
  canEdit,
  onRemove,
}: {
  label: string;
  chips: readonly PreferenceChip[];
  canEdit: boolean;
  onRemove: (ingredientId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-muted uppercase">{label}</span>
      {chips.length === 0 ? (
        <p className="text-sm text-muted">None yet.</p>
      ) : (
        <ul aria-label={label} className="flex flex-wrap gap-2">
          {chips.map((c) => (
            <li
              key={c.ingredientId}
              className="inline-flex min-h-11 items-center gap-1 rounded-full border border-line bg-surface pl-3.5 text-sm text-ink"
            >
              {c.name}
              {canEdit ? (
                <button
                  type="button"
                  aria-label={`Remove ${c.name} from ${label.toLowerCase()}`}
                  onClick={() => onRemove(c.ingredientId)}
                  className="inline-flex size-11 items-center justify-center rounded-full text-muted hover:text-fail"
                >
                  <span aria-hidden>×</span>
                </button>
              ) : (
                <span className="pr-3.5" />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StrengthBar({ strength }: { strength: number }) {
  const label = strengthLabel(strength);
  const pct = Math.max(8, Math.round(Math.min(1, strength) * 100));
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-muted">
      <span aria-hidden className="h-1.5 w-12 overflow-hidden rounded-full bg-line">
        <span className="block h-full rounded-full bg-brand" style={{ width: `${pct}%` }} />
      </span>
      {label}
    </span>
  );
}
